import React, { useState, useCallback } from "react";
import SimulationDataGeneratorAPI from "../services/SimulationDataGeneratorAPI";
import LoadingSpinner from "../common/LoadingSpinner";

/**
 * Phone Sanitizer tab — scan production messages for phone numbers and replace
 * them with 888-899-5004.
 *
 * Workflow:
 * 1. "Scan Messages" → dry-run; shows all messages with phone numbers
 * 2. Per-message checkboxes to include/exclude from replacement
 * 3. "Mark as Safe" per message → exempts it from future scans
 * 4. "Safe List" view → audit all exempted messages; revoke with "Remove Exemption"
 */
const PhoneSanitizer = () => {
  // ── view: "scan" | "safelist"
  const [view, setView] = useState("scan");

  // ── scan state
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [selectedUuids, setSelectedUuids] = useState(new Set());

  // ── safe-list state
  const [safeList, setSafeList] = useState(null);
  const [safeListLoading, setSafeListLoading] = useState(false);
  const [safeListError, setSafeListError] = useState(null);

  // ── per-message safe marking in progress
  const [markingSafe, setMarkingSafe] = useState(new Set());

  const formatChannel = (ch) => {
    if (!ch) return ch;
    return ch
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const handleChannelFilterChange = (value) => {
    setChannelFilter(value);
    setScanResult(null);
    setApplyResult(null);
    setSuccessMsg("");
    setError(null);
    setSelectedUuids(new Set());
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 5000);
  };

  // ── Run dry-run scan ──
  const handleScan = async () => {
    setLoading(true);
    setError(null);
    setScanResult(null);
    setApplyResult(null);
    setSuccessMsg("");
    setSelectedUuids(new Set());
    try {
      const result = await SimulationDataGeneratorAPI.scanPhoneNumbers(
        channelFilter || undefined,
      );
      setScanResult(result);
      if (result.results && result.results.length > 0) {
        setSelectedUuids(new Set(result.results.map((r) => r.uuid)));
      }
      if (result.totalScanned === 0) {
        setError(
          channelFilter
            ? `No ${formatChannel(channelFilter)} production messages found to scan.`
            : `No production messages found. Generate and approve messages first.`,
        );
      } else if (result.issuesFound === 0) {
        showSuccess(
          `All ${result.totalScanned} messages are clean — no phone numbers found.` +
            (result.skippedSafe > 0
              ? ` (${result.skippedSafe} skipped — marked safe)`
              : ``),
        );
      }
    } catch (err) {
      console.error("Phone scan failed:", err);
      setError("Phone number scan failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Toggle checkbox selection ──
  const toggleUuid = (uuid) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!scanResult?.results) return;
    setSelectedUuids(new Set(scanResult.results.map((r) => r.uuid)));
  };

  const handleDeselectAll = () => {
    setSelectedUuids(new Set());
  };

  const selectedReplacementCount = scanResult?.results
    ? scanResult.results
        .filter((r) => selectedUuids.has(r.uuid))
        .reduce((sum, r) => sum + r.replacementCount, 0)
    : 0;

  // ── Apply selected ──
  const handleApplySelected = async () => {
    const uuids = Array.from(selectedUuids);
    if (uuids.length === 0) return;
    if (
      !window.confirm(
        `This will replace ${selectedReplacementCount} phone number(s) across ${uuids.length} selected message(s) with 888-899-5004.\n\nThis action cannot be undone. Continue?`,
      )
    )
      return;

    setApplying(true);
    setError(null);
    try {
      const result =
        await SimulationDataGeneratorAPI.applyPhoneSanitizationSelected(uuids);
      setApplyResult(result);
      setScanResult(null);
      setSelectedUuids(new Set());
      showSuccess(
        `Phone sanitization complete: ${result.messagesFixed} message(s) updated, ${result.totalReplacements} phone number(s) replaced with 888-899-5004.`,
      );
    } catch (err) {
      console.error("Phone sanitization failed:", err);
      setError("Failed to apply phone sanitization. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  // ── Apply all ──
  const handleApplyAll = async () => {
    if (
      !window.confirm(
        `This will replace ${scanResult?.totalReplacements || 0} phone number(s) across ${scanResult?.issuesFound || 0} message(s) with 888-899-5004.\n\nThis action cannot be undone. Continue?`,
      )
    )
      return;

    setApplying(true);
    setError(null);
    try {
      const result = await SimulationDataGeneratorAPI.applyPhoneSanitization(
        channelFilter || undefined,
      );
      setApplyResult(result);
      setScanResult(null);
      setSelectedUuids(new Set());
      showSuccess(
        `Phone sanitization complete: ${result.messagesFixed} message(s) updated, ${result.totalReplacements} phone number(s) replaced with 888-899-5004.`,
      );
    } catch (err) {
      console.error("Phone sanitization failed:", err);
      setError("Failed to apply phone sanitization. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  // ── Mark a message as safe ──
  const handleMarkSafe = async (item) => {
    const reason = window.prompt(
      `Mark "${item.subject || item.uuid}" as safe?\n\nOptionally enter a reason (e.g. "This is our support line — intentional"):`,
      "",
    );
    if (reason === null) return; // cancelled

    setMarkingSafe((prev) => new Set(prev).add(item.uuid));
    try {
      await SimulationDataGeneratorAPI.markPhoneSafe(
        item.uuid,
        reason.trim() || undefined,
      );
      // Remove from scan results immediately
      setScanResult((prev) => {
        if (!prev) return prev;
        const filtered = prev.results.filter((r) => r.uuid !== item.uuid);
        return {
          ...prev,
          results: filtered,
          issuesFound: Math.max(0, prev.issuesFound - 1),
          skippedSafe: (prev.skippedSafe || 0) + 1,
          totalReplacements: prev.totalReplacements - item.replacementCount,
        };
      });
      setSelectedUuids((prev) => {
        const next = new Set(prev);
        next.delete(item.uuid);
        return next;
      });
      showSuccess(
        `Message marked as safe — it will be skipped in future scans.`,
      );
    } catch (err) {
      console.error("Mark safe failed:", err);
      setError("Failed to mark message as safe. Please try again.");
    } finally {
      setMarkingSafe((prev) => {
        const next = new Set(prev);
        next.delete(item.uuid);
        return next;
      });
    }
  };

  // ── Load safe list ──
  const handleLoadSafeList = useCallback(async () => {
    setSafeListLoading(true);
    setSafeListError(null);
    try {
      const list = await SimulationDataGeneratorAPI.getPhoneSafeList();
      setSafeList(list);
    } catch (err) {
      console.error("Safe list load failed:", err);
      setSafeListError("Failed to load safe list. Please try again.");
    } finally {
      setSafeListLoading(false);
    }
  }, []);

  const handleViewSafeList = () => {
    setView("safelist");
    handleLoadSafeList();
  };

  // ── Remove safe exemption ──
  const handleUnmarkSafe = async (entry) => {
    if (
      !window.confirm(
        `Remove safe exemption from "${entry.subject || entry.messageUuid}"?\n\nThis message will be included in future phone-number scans.`,
      )
    )
      return;

    try {
      await SimulationDataGeneratorAPI.unmarkPhoneSafe(entry.messageUuid);
      setSafeList((prev) =>
        prev.filter((e) => e.messageUuid !== entry.messageUuid),
      );
      showSuccess(
        `Safe exemption removed — the message will be scanned next time.`,
      );
    } catch (err) {
      console.error("Unmark safe failed:", err);
      setSafeListError("Failed to remove exemption. Please try again.");
    }
  };

  const toggleExpand = (uuid) => {
    setExpandedId(expandedId === uuid ? null : uuid);
  };

  const displayResult = applyResult || scanResult;
  const isApplyResult = !!applyResult;
  const allSelected =
    scanResult?.results?.length > 0 &&
    scanResult.results.every((r) => selectedUuids.has(r.uuid));
  const noneSelected = selectedUuids.size === 0;

  // ══════════════════════════════════════════════════════════════════════
  // SAFE LIST VIEW
  // ══════════════════════════════════════════════════════════════════════
  if (view === "safelist") {
    return (
      <div className="compliance-scanner">
        <div className="compliance-controls">
          <div
            className="compliance-controls-row"
            style={{ alignItems: "center" }}
          >
            <h3 style={{ margin: 0 }}>
              Phone Safe List
              {safeList ? ` (${safeList.length})` : ""}
            </h3>
            <div className="compliance-actions">
              <button
                className="btn-compliance-audit"
                onClick={handleLoadSafeList}
                disabled={safeListLoading}
              >
                {safeListLoading ? "Refreshing..." : "Refresh"}
              </button>
              <button
                className="btn-compliance-audit"
                onClick={() => setView("scan")}
                style={{ marginLeft: 8 }}
              >
                ← Back to Scanner
              </button>
            </div>
          </div>
          <p className="compliance-description">
            Messages listed here are exempt from phone-number sanitization
            scans. Current phone numbers found in each message are shown live —
            if you see something non-compliant, remove the exemption and run a
            fresh scan.
          </p>
        </div>

        {safeListLoading && (
          <div className="compliance-loading">
            <LoadingSpinner />
            <p>Loading safe list…</p>
          </div>
        )}
        {safeListError && (
          <div className="compliance-error">{safeListError}</div>
        )}
        {successMsg && <div className="compliance-success">{successMsg}</div>}

        {safeList && !safeListLoading && safeList.length === 0 && (
          <div className="compliance-success">
            No messages are currently marked as safe.
          </div>
        )}

        {safeList && safeList.length > 0 && (
          <div className="compliance-results">
            {safeList.map((entry) => (
              <div
                key={entry.messageUuid}
                className="compliance-result-card has-issues"
              >
                <div
                  className="compliance-result-header"
                  style={{ cursor: "pointer" }}
                >
                  <div
                    className="compliance-result-title"
                    style={{ flex: 1 }}
                    onClick={() => toggleExpand(entry.messageUuid)}
                  >
                    <span className="compliance-badge compliance-badge-ok">
                      SAFE
                    </span>
                    <strong>{entry.subject || "(no subject)"}</strong>
                    <span className="compliance-result-channel">
                      {formatChannel(entry.communicationChannel)}
                    </span>
                    {entry.phonesFound && entry.phonesFound.length > 0 && (
                      <span
                        className="compliance-badge compliance-badge-warning"
                        style={{ marginLeft: 8 }}
                      >
                        {entry.phonesFound.length} number
                        {entry.phonesFound.length !== 1 ? "s" : ""} still
                        present
                      </span>
                    )}
                  </div>
                  <button
                    className="btn-compliance-fix"
                    style={{ padding: "3px 10px", fontSize: "0.8rem" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnmarkSafe(entry);
                    }}
                  >
                    Remove Exemption
                  </button>
                  <span
                    className="compliance-expand-icon"
                    onClick={() => toggleExpand(entry.messageUuid)}
                    style={{ marginLeft: 8 }}
                  >
                    {expandedId === entry.messageUuid ? "▼" : "▶"}
                  </span>
                </div>

                {expandedId === entry.messageUuid && (
                  <div className="compliance-result-details">
                    {entry.phonesFound && entry.phonesFound.length > 0 ? (
                      <div className="compliance-detail-section">
                        <h4>Phone Numbers Currently Present</h4>
                        <ul>
                          {entry.phonesFound.map((phone, i) => (
                            <li key={i}>
                              <code>{phone}</code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="compliance-detail-section">
                        <p style={{ color: "#2e7d32" }}>
                          ✓ No phone numbers currently detected in this message.
                        </p>
                      </div>
                    )}
                    <div
                      className="compliance-detail-meta"
                      style={{
                        fontSize: "0.82rem",
                        color: "#888",
                        marginTop: 8,
                      }}
                    >
                      UUID: {entry.messageUuid}
                      {entry.reason && (
                        <span style={{ marginLeft: 12 }}>
                          Reason: <em>{entry.reason}</em>
                        </span>
                      )}
                      <span style={{ marginLeft: 12 }}>
                        Marked safe: {formatDate(entry.markedAt)} by{" "}
                        {entry.markedBy}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCAN VIEW
  // ══════════════════════════════════════════════════════════════════════
  return (
    <div className="compliance-scanner">
      {/* ── Controls ── */}
      <div className="compliance-controls">
        <div className="compliance-controls-row">
          <div
            className="form-group"
            style={{ minWidth: 180, flex: "0 1 auto" }}
          >
            <label htmlFor="phone-channel-filter">Channel Filter</label>
            <select
              id="phone-channel-filter"
              value={channelFilter}
              onChange={(e) => handleChannelFilterChange(e.target.value)}
              className="form-select"
            >
              <option value="">All Channels</option>
              <option value="EMAIL">Email</option>
              <option value="SMS">SMS</option>
              <option value="VOICE">Voice</option>
              <option value="SOCIAL_MEDIA">Social Media</option>
            </select>
          </div>

          <div className="compliance-actions">
            <button
              className="btn-compliance-audit"
              onClick={handleScan}
              disabled={loading || applying}
            >
              {loading ? "Scanning..." : "Scan Messages"}
            </button>

            {scanResult && scanResult.issuesFound > 0 && (
              <>
                <button
                  className="btn-compliance-fix"
                  onClick={handleApplySelected}
                  disabled={applying || noneSelected}
                  title={
                    noneSelected ? "Select at least one message" : undefined
                  }
                >
                  {applying
                    ? "Replacing..."
                    : `Replace Selected (${selectedReplacementCount} number${selectedReplacementCount !== 1 ? "s" : ""})`}
                </button>

                <button
                  className="btn-compliance-audit"
                  onClick={handleApplyAll}
                  disabled={applying}
                  style={{ marginLeft: 8 }}
                >
                  {applying
                    ? "Replacing..."
                    : `Replace All (${scanResult.totalReplacements} numbers)`}
                </button>
              </>
            )}

            <button
              className="btn-compliance-audit"
              onClick={handleViewSafeList}
              style={{ marginLeft: 8 }}
              title="View and audit messages marked as safe"
            >
              Safe List
            </button>
          </div>
        </div>

        <p className="compliance-description">
          Scans all production messages for phone numbers — both standard digit
          formats (800-555-1234) and vanity/alphanumeric formats
          (1-800-PAY-HOME). All detected phone numbers will be replaced with{" "}
          <strong>888-899-5004</strong>. Run the scan first to preview findings,
          uncheck false positives or mark them as Safe, then apply replacements.
        </p>
      </div>

      {/* ── Loading / Error / Success ── */}
      {loading && (
        <div className="compliance-loading">
          <LoadingSpinner />
          <p>Scanning messages for phone numbers...</p>
        </div>
      )}
      {error && <div className="compliance-error">{error}</div>}
      {successMsg && <div className="compliance-success">{successMsg}</div>}

      {/* ── Summary Card ── */}
      {displayResult && !loading && (
        <div className="compliance-summary">
          <h3>{isApplyResult ? "Replacement Results" : "Scan Results"}</h3>
          <div className="compliance-summary-grid">
            <div className="compliance-stat">
              <span className="compliance-stat-value">
                {displayResult.totalScanned}
              </span>
              <span className="compliance-stat-label">Scanned</span>
            </div>
            <div className="compliance-stat">
              <span className="compliance-stat-value compliance-stat-issues">
                {displayResult.issuesFound}
              </span>
              <span className="compliance-stat-label">
                {isApplyResult ? "Had Phone Numbers" : "With Phone Numbers"}
              </span>
            </div>
            <div className="compliance-stat">
              <span className="compliance-stat-value compliance-stat-compliant">
                {displayResult.alreadyClean}
              </span>
              <span className="compliance-stat-label">Already Clean</span>
            </div>
            {displayResult.skippedSafe > 0 && (
              <div className="compliance-stat">
                <span
                  className="compliance-stat-value"
                  style={{ color: "#1976d2" }}
                >
                  {displayResult.skippedSafe}
                </span>
                <span className="compliance-stat-label">Skipped (Safe)</span>
              </div>
            )}
            {isApplyResult && (
              <div className="compliance-stat">
                <span className="compliance-stat-value compliance-stat-fixed">
                  {displayResult.messagesFixed}
                </span>
                <span className="compliance-stat-label">Updated</span>
              </div>
            )}
            <div className="compliance-stat">
              <span
                className={`compliance-stat-value ${displayResult.totalReplacements > 0 ? "compliance-stat-issues" : ""}`}
              >
                {displayResult.totalReplacements}
              </span>
              <span className="compliance-stat-label">
                {isApplyResult ? "Numbers Replaced" : "Numbers Found"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Per-Message Details ── */}
      {displayResult &&
        displayResult.results &&
        displayResult.results.length > 0 && (
          <div className="compliance-results">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>
                {isApplyResult
                  ? "Updated Messages"
                  : "Messages with Phone Numbers"}{" "}
                ({displayResult.results.length})
              </h3>

              {!isApplyResult && displayResult.results.length > 0 && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn-compliance-audit"
                    style={{ padding: "4px 10px", fontSize: "0.82rem" }}
                    onClick={handleSelectAll}
                    disabled={allSelected}
                  >
                    Select All
                  </button>
                  <button
                    className="btn-compliance-audit"
                    style={{ padding: "4px 10px", fontSize: "0.82rem" }}
                    onClick={handleDeselectAll}
                    disabled={noneSelected}
                  >
                    Deselect All
                  </button>
                </div>
              )}
            </div>

            {displayResult.results.map((item) => (
              <div
                key={item.uuid}
                className={`compliance-result-card ${item.hadIssues ? "has-issues" : "compliant"}`}
                style={{
                  opacity:
                    !isApplyResult && !selectedUuids.has(item.uuid) ? 0.55 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                <div
                  className="compliance-result-header"
                  style={{ cursor: "pointer" }}
                >
                  {!isApplyResult && (
                    <input
                      type="checkbox"
                      checked={selectedUuids.has(item.uuid)}
                      onChange={() => toggleUuid(item.uuid)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        marginRight: 10,
                        cursor: "pointer",
                        width: 16,
                        height: 16,
                        flexShrink: 0,
                      }}
                      title={
                        selectedUuids.has(item.uuid)
                          ? "Uncheck to skip this message"
                          : "Check to include this message"
                      }
                    />
                  )}

                  <div
                    className="compliance-result-title"
                    style={{ flex: 1 }}
                    onClick={() => toggleExpand(item.uuid)}
                  >
                    <span
                      className={`compliance-badge ${item.hadIssues ? "compliance-badge-warning" : "compliance-badge-ok"}`}
                    >
                      {item.replacementCount} phone number
                      {item.replacementCount !== 1 ? "s" : ""}
                    </span>
                    <strong>{item.subject || "(no subject)"}</strong>
                    <span className="compliance-result-channel">
                      {formatChannel(item.communicationChannel)}
                    </span>
                  </div>

                  {/* Mark as Safe button — only in scan (non-apply) mode */}
                  {!isApplyResult && (
                    <button
                      className="btn-compliance-audit"
                      style={{
                        padding: "3px 10px",
                        fontSize: "0.8rem",
                        marginLeft: 8,
                        flexShrink: 0,
                      }}
                      disabled={markingSafe.has(item.uuid)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkSafe(item);
                      }}
                      title="Mark this message as safe — it will be skipped in future scans"
                    >
                      {markingSafe.has(item.uuid) ? "Saving…" : "Mark as Safe"}
                    </button>
                  )}

                  <span
                    className="compliance-expand-icon"
                    onClick={() => toggleExpand(item.uuid)}
                    style={{ marginLeft: 8 }}
                  >
                    {expandedId === item.uuid ? "▼" : "▶"}
                  </span>
                </div>

                {expandedId === item.uuid && (
                  <div className="compliance-result-details">
                    <div className="compliance-detail-section">
                      <h4>Phone Numbers Found</h4>
                      <ul>
                        {item.phonesFound.map((phone, i) => (
                          <li key={i}>
                            <code>{phone}</code> → <strong>888-899-5004</strong>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="compliance-detail-section">
                      <h4>Fields Affected</h4>
                      <ul>
                        {item.fieldsAffected.map((field, i) => (
                          <li key={i}>
                            <code>{field}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div
                      className="compliance-detail-meta"
                      style={{
                        fontSize: "0.82rem",
                        color: "#888",
                        marginTop: 8,
                      }}
                    >
                      UUID: {item.uuid} · Message ID: {item.messageId}
                      {item.wasUpdated && (
                        <span style={{ color: "#2e7d32", marginLeft: 8 }}>
                          ✓ Updated
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default PhoneSanitizer;
